/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useQuery } from '@apollo/client'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useState } from 'react'

import {
  GET_PRODUCTS,
  GetProductsQuery,
  GetProductsQueryVariables,
  PlanogramChangeDetailsFragment,
} from '@/graphql'
import useT from '@/i18n/useT'

import { shelves } from './planogram-history.helpers'
import RenderedPlanogram from './RenderedPlanogram'

export default function PlanogramHistoryVisual({
  change,
}: {
  change: PlanogramChangeDetailsFragment
}) {
  const { t } = useT()
  const hasShortViewport = useMediaQuery('(max-height:800px)')
  const [version, setVersion] = useState<'current' | 'previous'>('current')
  const previous = shelves(change.previousPlanogram.shapedUpcs)
  const current = shelves(change.newPlanogram.shapedUpcs)
  const selected = version === 'current' ? current : previous
  const upcs = Array.from(
    new Set([...previous.flat(), ...current.flat()].filter(Boolean)),
  )
  const { data, loading } = useQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS, {
    variables: { filters: { upcs } },
    skip: upcs.length === 0,
  })
  const productByUpc = Object.fromEntries(
    (data?.products?.results ?? []).map(product => [product.upcA, product]),
  )
  const products = selected.map(shelf =>
    shelf.map(
      upc =>
        productByUpc[upc] ?? {
          upcA: upc,
          planogramImageUrl: null,
          marketingImageUrl: null,
        },
    ),
  )
  const highlightByPosition: Record<
    string,
    { bgColor: string; borderColor: string }
  > = {}
  const shelfCount = Math.max(previous.length, current.length)
  for (let shelfIndex = 0; shelfIndex < shelfCount; shelfIndex += 1) {
    const facingCount = Math.max(
      previous[shelfIndex]?.length ?? 0,
      current[shelfIndex]?.length ?? 0,
    )
    for (let columnIndex = 0; columnIndex < facingCount; columnIndex += 1) {
      if (
        previous[shelfIndex]?.[columnIndex] !==
        current[shelfIndex]?.[columnIndex]
      ) {
        highlightByPosition[`${shelfIndex}-${columnIndex}`] = {
          bgColor: 'rgba(46, 160, 67, 0.28)',
          borderColor: '#238636',
        }
      }
    }
  }

  const renderProduct = (label: string, upc?: string) => {
    const product = upc ? productByUpc[upc] : undefined
    const imageUrl = product?.planogramImageUrl || product?.marketingImageUrl
    const emptyLabel = t('planogramHistory.product.empty', 'Empty')
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 150,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Typography variant="subtitle2">{label}</Typography>
        {upc ? (
          <Box
            component="img"
            src={
              imageUrl ||
              '/static/img/products/unknown-product-planogram-facing.png'
            }
            alt={product?.preferredName || upc}
            sx={{ width: '100%', height: 140, objectFit: 'contain' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 140,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="text.secondary">{emptyLabel}</Typography>
          </Box>
        )}
        <Typography variant="body2" fontWeight={600} textAlign="center">
          {product?.preferredName ||
            product?.packagingName ||
            upc ||
            emptyLabel}
        </Typography>
        {upc && (
          <Typography variant="caption" color="text.secondary">
            {upc}
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, whiteSpace: 'nowrap' }}
        >
          {t('planogramHistory.dialog.planogram', 'Planogram')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={version}
          onChange={(_, value) => value && setVersion(value)}
          sx={{ flexShrink: 0 }}
        >
          <ToggleButton
            value="previous"
            sx={{ width: { xs: 104, sm: 120 }, px: 0.5 }}
          >
            <CompareArrowsIcon fontSize="small" sx={{ mr: 0.25 }} />
            {t('planogramHistory.dialog.previous', 'Previous')}
          </ToggleButton>
          <ToggleButton
            value="current"
            sx={{ width: { xs: 76, sm: 92 }, px: 0.5 }}
          >
            {t('planogramHistory.dialog.current', 'Current')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <RenderedPlanogram
        products={products}
        loadingByUpc={
          loading ? Object.fromEntries(upcs.map(upc => [upc, true])) : undefined
        }
        highlightByPosition={highlightByPosition}
        popoverEnabledByPosition={Object.fromEntries(
          Object.keys(highlightByPosition).map(key => [key, true]),
        )}
        renderPopoverContent={(_, positionKey) => {
          if (!positionKey) return null
          const [shelfIndex, columnIndex] = positionKey.split('-').map(Number)
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                boxShadow: 6,
              }}
            >
              {renderProduct(
                t('planogramHistory.dialog.before', 'Before'),
                previous[shelfIndex]?.[columnIndex],
              )}
              <Typography variant="h5">→</Typography>
              {renderProduct(
                t('planogramHistory.dialog.after', 'After'),
                current[shelfIndex]?.[columnIndex],
              )}
            </Box>
          )
        }}
        maxHeight={hasShortViewport ? 360 : 480}
        shelfHeight={80}
      />
    </Box>
  )
}
